/**
 * PlayerSheet — the player list as a bottom sheet, below 640px only.
 *
 * 🔴 WHY THE PANEL CANNOT JUST STACK ON A PHONE. Below PANEL_BREAKPOINT the
 * matrix goes full width and PlayerPanel drops underneath it, which on a 412px
 * screen puts the list roughly sixteen roster rows below the cell you tapped.
 * Tapping a cell appeared to do nothing: the selection was real, the panel had
 * re-scoped itself to that slot, and none of it was on screen. A phone needs the
 * list to come to the cell rather than the other way round.
 *
 * So the sheet is anchored to the BOTTOM of the viewport and capped at 85% of
 * its height, which leaves a strip of the page visible above it. That strip is
 * doing work: it is what says the sheet is over the plan rather than a new
 * screen, and it is where the scrim tap lands.
 *
 * 🔴 THE SCRIM CLOSES AND CHANGES NOTHING. Adding a player is the only thing
 * that writes; dismissing is always free. That is why the close paths are a
 * scrim tap and an X rather than a "cancel" that implies there is something to
 * cancel.
 *
 * Purely a container: it knows nothing about players, slots or the plan. The
 * scoping is PlayerPanel's, off the `slot` prop it already takes.
 */

import React, { useEffect } from "react";
import { theme } from "../../data/theme";

export default function PlayerSheet({ open, onClose, title, subtitle, children }) {
  // Escape closes it too. Bound only while open, the same shape PlanMenu and
  // OptimizeSheet use, so there is one dismiss behaviour in this feature.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        data-testid="player-sheet-scrim"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: theme.helpers.overlay(0.6),
        }}
      />
      <div
        data-testid="player-sheet"
        role="dialog"
        aria-label="Add a player"
        // 🔴 STOPS ITS OWN CLICKS. The scrim is a sibling rather than a parent,
        // so this is belt and braces today — but a sheet that dismissed itself
        // when you tapped a player row would be the worst possible bug here, and
        // the guard costs nothing.
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 201,
          maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          background: theme.colors.bg.app,
          borderTop: `1px solid ${theme.colors.border.default}`,
          borderTopLeftRadius: theme.radius.xl,
          borderTopRightRadius: theme.radius.xl,
          boxShadow: "0 -18px 44px rgba(0,0,0,0.55)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: theme.spacing.md,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          borderBottom: `1px solid ${theme.colors.border.subtle}`,
          flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.heavy,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: theme.colors.text.faint,
            }}>{title}</div>
            {subtitle && (
              <div style={{
                fontSize: theme.fontSize.small,
                color: theme.colors.text.muted,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            data-testid="player-sheet-close"
            aria-label="Close"
            onClick={onClose}
            style={{
              flexShrink: 0,
              width: 32, height: 32,
              borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.border.default}`,
              background: theme.colors.bg.raised,
              color: theme.colors.text.secondary,
              fontFamily: theme.fonts.body,
              fontSize: theme.fontSize.sub,
              cursor: "pointer",
            }}
          >✕</button>
        </div>

        {/* The list scrolls, the header does not. */}
        <div style={{ overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {children}
        </div>
      </div>
    </>
  );
}
